import { useState, useEffect, useCallback, useRef } from 'react';
import LimpopoMap from '../LimpopoMap/LimpopoMap';
import { ENDPOINTS } from '../../api/api';
import styles from './MapDevicesPanel.module.css';

function MapDevicesPanel({ district, circuit, school } = {}) {
  const [markers, setMarkers]   = useState([]);
  const [status, setStatus]     = useState({ total: 0, online: 0, offline: 0 });
  const debounceRef             = useRef(null);
  const isGlobal                = !district && !circuit && !school;

  // Fetch real total / online / offline counts for this scope
  useEffect(() => {
    const scope = district ? { district: district.toUpperCase() }
                : circuit  ? { circuit:  circuit.toUpperCase()  }
                : school   ? { school:   school.toUpperCase()   }
                : null;
    fetch(ENDPOINTS.status(scope))
      .then((r) => r.json())
      .then((s) => setStatus(s))
      .catch(console.error);
  }, [district, circuit, school]);

  const fetchMarkers = useCallback((url) => {
    fetch(url)
      .then((r) => r.json())
      .then((raw) => {
        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : null);
        if (!list) return;
        setMarkers(list.map((d) => ({
          imei: d.imeiNumber,
          lat:  d.realTimeStats?.currentLatitude  ?? 0,
          lng:  d.realTimeStats?.currentLongitude ?? 0,
        })));
      })
      .catch(console.error);
  }, []);

  // Initial markers load
  useEffect(() => {
    let url;
    if (school)        url = ENDPOINTS.schoolMap(school.toUpperCase());
    else if (circuit)  url = ENDPOINTS.circuitMap(circuit.toUpperCase());
    else if (district) url = ENDPOINTS.districtMap(district.toUpperCase());
    else               url = ENDPOINTS.globalMap();
    fetchMarkers(url);
  }, [district, circuit, school, fetchMarkers]);

  // Lazy-load markers on pan/zoom — global map only
  const handleBoundsChange = useCallback((bounds) => {
    if (!isGlobal) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchMarkers(ENDPOINTS.globalMap(bounds));
    }, 400);
  }, [isGlobal, fetchMarkers]);

  const { total, online, offline } = status;
  const onlinePct  = total ? ((online  / total) * 100).toFixed(1) : '0.0';
  const offlinePct = total ? ((offline / total) * 100).toFixed(1) : '0.0';

  return (
    <div className={styles.row}>
      <LimpopoMap markers={markers} onBoundsChange={isGlobal ? handleBoundsChange : undefined} />

      <div className={styles.devicesPanel}>
        <div className={styles.title}>Devices</div>
        <div className={styles.cards}>
          <div className={styles.card}>
            <div className={styles.label}>
              <span className={styles.dot} style={{ background: 'var(--color-text-secondary)' }} />
              Total
            </div>
            <div className={styles.value}>{(total ?? 0).toLocaleString()}</div>
          </div>
          <div className={styles.card}>
            <div className={styles.label}>
              <span className={styles.dot} style={{ background: 'var(--color-primary)' }} />
              Online
            </div>
            <div className={styles.value}>
              {(online ?? 0).toLocaleString()}
              <span className={styles.pct}>{onlinePct}%</span>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.label}>
              <span className={styles.dot} style={{ background: '#E53535' }} />
              Offline
            </div>
            <div className={styles.value}>
              {(offline ?? 0).toLocaleString()}
              <span className={styles.pct}>{offlinePct}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapDevicesPanel;
